<template>
    <div class="pay-for-bill-popover">
        <div class="closer" @click="closePopover">
            <i class="fal fa-times"></i>
        </div>

        <ScrollableView>
            <TipAndPayForm
                class="tip-and-pay-form"
                :class="{ hidden: !showTipView }"
                v-model:tip="tip"
                :totalConsumption="consumption"
                :disabled="payment.isProcessingPayment"
            />

            <ApplyDiscountSection
                :inOperatorApp="true"
                :locationId="bill.locationId!"
                class="operator"
                @applyDiscountErrorOccurred="onApplyDiscountErrorOccurred"
                v-if="useVouchersAndDiscounts"
                :billId="billId"
                :enable-qr-scanner="true"
                :onDiscountAppliedToBill="onDiscountAppliedToBill"
            />

            <QuickPaymentSelector
                v-if="showQuickPaymentMethods"
                :disabled="isPaymentButtonDisabled"
                :quickPaymentMethods="quickPaymentMethods"
                @onSelected="onQuickPaymentMethodSelected"
            />

            <TipSummaryTable
                class="tip-summary-table"
                :tip="tip"
                :fees="fees"
                :totalConsumption="consumption"
                :discountAmount="discountAmount"
                :showTip="true"
                :hasDiscounts="hasDiscounts"
                :isDiscountClickable="true"
                @discountClick="onDiscountClick"
            />
            <BillDiscountListModal ref="billDiscountListModalComponent" @discountRemoved="onDiscountRemoved" />
        </ScrollableView>

        <footer>
            <PaymentMethodSelector
                ref="paymentMethodSelectorComponent"
                v-model:selectedMethod="selectedPaymentMethod"
                :availableMethods="paymentIntent?.paymentMethods"
                :isLoading="payment.isLoadingPaymentIntent"
                :useDiscountsPaymentMethod="isTotalFullyCoveredByDiscounts"
                :hasVouchers="bill.hasVouchers"
                :autoClosePopoverOnSelectPaymentMethod="true"
                @onSelected="onPaymentMethodSelected"
            />

            <!-- button to display when no payment method is selected - looks disabled but if you click it the payment method selector will open -->
            <ProceedButton
                class="pay-button"
                :currentPrice="currentTotalPrice"
                :disabled="isPaymentButtonDisabled"
                @click="onPayButtonClick"
            >
                <span v-if="!payment.isProcessingPayment">{{ translations.pay.payNow }}</span>
                <span v-else>{{ trans_vue.addToCartPopover.adding }}</span>
            </ProceedButton>
        </footer>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import { dataEvents } from "@/data/events/dataEvents";
    import type { ISelectedItem } from "@/data/model/Bill";
    import type { IPaymentIntent, IPaymentMethod } from "@/data/model/Customer";
    import type { IBillDiscountChangedEvent } from "@/data/model/Events";
    import type { IBillDiscountAppliedEventData } from "@/data/model/Voucher";
    import { onApplyDiscountErrorOccurred } from "@/data/services/DiscountsService";
    import {
        getIsApplyVouchersAllowedForPayUseCase,
        getIsTipAllowed,
        isTipAllowedForService,
    } from "@/data/services/PaymentService";
    import { useBillStore } from "@/data/stores/bill";
    import { usePaymentStore } from "@/data/stores/payment";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { useTimersStore } from "@/data/stores/timers";
    import { trans_vue } from "@/operator/localization/TransVue";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

    import ProceedButton from "@/components/elements/ProceedButton.vue";
    import ScrollableView from "@/components/layout/ScrollableView.vue";
    import QuickPaymentSelector from "@/components/pay/QuickPaymentSelector.vue";
    import TipAndPayForm from "@/components/pay/TipAndPayForm.vue";
    import TipSummaryTable from "@/components/pay/TipSummaryTable.vue";
    import ApplyDiscountSection from "@/components/vouchers/ApplyDiscountSection.vue";
    import BillDiscountListModal from "@/operator/components/payment/BillDiscountListModal.vue";
    import PaymentMethodSelector from "@/operator/components/payment/PaymentMethodSelector.vue";

    // define stores
    const payment = usePaymentStore();
    const bill = useBillStore();
    const alloc = useTableAllocationStore();
    const timers = useTimersStore();

    // define properties
    const props = defineProps<{
        billId: string;
        consumption: number;
        fees: number;
        totalPayableAfterDiscount: number;
        selectedItems: ISelectedItem[];
        discountAmount: number;
        hasDiscounts: boolean;
    }>();

    // define emitted events
    const emit = defineEmits(["closed", "paymentSuccess"]);

    // define reactive variables
    const billId = ref(props.billId);
    const consumption = ref(props.consumption);
    const fees = ref(props.fees);
    const totalPayableAfterDiscount = ref(props.totalPayableAfterDiscount);
    const tip = ref(null as number | null);
    const discountAmount = ref(props.discountAmount);
    const hasDiscounts = ref(props.hasDiscounts);
    const selectedItems = ref(props.selectedItems);

    const selectedPaymentMethod = ref(null as IPaymentMethod | null);
    const paymentIntent = ref(null as IPaymentIntent | null);

    // define computed
    const isTotalFullyCoveredByDiscounts = computed(() => bill.isFullyCoveredByDiscounts && (tip.value ?? 0) == 0);

    // The tip field should be shown only if the setting tip is enabled for selected service
    const showTipView = computed(() => {
        return isTipAllowedForService(payment.paymentService);
    });

    // determines whether the tip should be cleared or not
    const isTipAllowed = computed(() => {
        return getIsTipAllowed(payment.paymentService, selectedPaymentMethod.value);
    });

    const useVouchersAndDiscounts = computed(() => {
        return getIsApplyVouchersAllowedForPayUseCase(selectedPaymentMethod.value, payment.paymentService?.payUseCase);
    });

    const hasQuickPaymentMethod = computed(
        () => paymentIntent.value != null && paymentIntent.value.quickPaymentMethods.length > 0
    );
    const showQuickPaymentMethods = computed(
        () => hasQuickPaymentMethod.value && !isTotalFullyCoveredByDiscounts.value
    );
    const quickPaymentMethods = computed(() => paymentIntent.value?.quickPaymentMethods ?? Array<IPaymentMethod>());
    const currentTotalPrice = computed(() => totalPayableAfterDiscount.value + (tip.value ?? 0));
    const isPaymentMethodSelected = computed(() => selectedPaymentMethod.value != null);
    const isPaymentButtonDisabled = computed(() => payment.isProcessingPayment || isSelectingPaymentMethod.value);
    const isSelectingPaymentMethod = computed(() => paymentMethodSelectorComponent.value?.isOpen ?? false);

    // define component refs
    const paymentMethodSelectorComponent = ref<InstanceType<typeof PaymentMethodSelector> | null>(null);
    const billDiscountListModalComponent = ref<InstanceType<typeof BillDiscountListModal> | null>(null);

    // define methods
    async function onPayButtonClick() {
        // if no payment method is selected - open payment method selector
        if (!isPaymentMethodSelected.value && paymentMethodSelectorComponent.value != null) {
            paymentMethodSelectorComponent.value.open();
        } else {
            await pay();
        }
    }

    async function pay() {
        if (tip.value == null) {
            tip.value = 0;
        }

        try {
            await payment.pay(
                consumption.value,
                discountAmount.value,
                tip.value,
                selectedItems.value,
                selectedPaymentMethod!.value!
            );
            emit("paymentSuccess");
            closePopover();
        } catch (ex) {
            logError(ex);
            showError(ex, translations.general.error); //todo: do better error handling
        }
    }

    function closePopover() {
        emit("closed");
    }

    function resetTipIfNeeded() {
        if (!isTipAllowed.value) {
            tip.value = null;
        }
    }

    async function onQuickPaymentMethodSelected(newPaymentMethod: IPaymentMethod) {
        if (newPaymentMethod != null && newPaymentMethod.type === "OpenCashDrawer") {
            await openCashDrawer();
        } else {
            selectedPaymentMethod.value = newPaymentMethod;
            resetTipIfNeeded();
            await pay();
        }
    }

    async function onPaymentMethodSelected(newPaymentMethod: IPaymentMethod) {
        if (newPaymentMethod != null && newPaymentMethod.type === "OpenCashDrawer") {
            await openCashDrawer();
        }
    }

    async function openCashDrawer() {
        try {
            const roomId = alloc.roomId;
            if (roomId == null) {
                logError("Room id not specified. The command to send open cash drawer cannot be sent");
                return;
            }
            const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
            await operatorDao.sendOpenCashDrawerToPrinterCommand({ roomId: roomId });
        } catch (e) {
            showError(e, translations.general.operationFailed);
        }
    }

    function showError(e, message) {
        logError(e);
        Popup.show({
            title: translations.general.error,
            message: message,
        });
    }

    async function loadPaymentIntent() {
        try {
            paymentIntent.value = await payment.prepareForCheckout(billId.value, tip.value ?? 0);

            // preparing the payment stops the running timers of the selection, which fixes their prices: the amounts
            // shown and paid are the ones the server prepared the payment for
            if (paymentIntent.value.selectionConsumption !== undefined) {
                consumption.value = paymentIntent.value.selectionConsumption;
                totalPayableAfterDiscount.value = paymentIntent.value.selectionTotalAfterDiscount!;
                discountAmount.value = paymentIntent.value.selectionTotalDiscountsAndVouchersAmount!;
            }

            // the stopped timers are not running anymore, which the offer list has to show
            await timers.load();
        } catch (ex) {
            logError(ex);
            showError(ex, translations.general.error); //todo: do better error handling
        }
    }

    onMounted(async () => {
        timers.isStartBlocked = true;
        await loadPaymentIntent();

        dataEvents.operatorSocket.BillDiscountChanged.add(onDiscountChanged);
    });

    onBeforeUnmount(() => {
        timers.isStartBlocked = false;
        dataEvents.operatorSocket.BillDiscountChanged.remove(onDiscountChanged);
    });

    watch(isTotalFullyCoveredByDiscounts, () => {
        if (!isTotalFullyCoveredByDiscounts.value) {
            selectedPaymentMethod.value = null;
            paymentIntent.value = null;
            loadPaymentIntent();
        }
    });

    watch(selectedPaymentMethod, () => {
        resetTipIfNeeded();
    });

    function onDiscountChanged(eventData: IBillDiscountChangedEvent) {
        console.log("onDiscountChanged");
        hasDiscounts.value = eventData.bill.hasDiscount;
        totalPayableAfterDiscount.value = eventData.bill.selectionTotalAfterDiscount;
        discountAmount.value = eventData.bill.selectionTotalDiscountsAndVouchersAmount;
    }

    function onDiscountAppliedToBill(eventData: IBillDiscountAppliedEventData) {
        console.log("onDiscountAppliedToBill");
        hasDiscounts.value = eventData.bill.hasDiscount;
        totalPayableAfterDiscount.value = eventData.bill.selectionTotalAfterDiscount;
        discountAmount.value = eventData.bill.selectionTotalDiscountsAndVouchersAmount;
    }

    function onDiscountClick() {
        billDiscountListModalComponent.value?.show();
    }

    function onDiscountRemoved() {
        hasDiscounts.value = bill.currentBill?.hasDiscount === true;
        totalPayableAfterDiscount.value = bill.currentBill!.selectionTotalAfterDiscount;
        discountAmount.value = bill.currentBill!.selectionTotalDiscountsAndVouchersAmount;
    }
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";

    .pay-for-bill-popover {
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        top: 62px;
        background: white;
        padding-top: 62px;

        display: flex;
        flex-direction: column;

        > .closer {
            position: absolute;
            right: 0;
            top: 0;
            height: 56px;
            width: 62px;
            cursor: pointer;
            text-align: center;

            > .fal {
                line-height: 56px;
                color: colors.$fontColor;
                font-size: 30px;
            }

            @include mixins.hover-and-active() {
                > .fal {
                    color: colors.$lightFontColor;
                }
            }
        }

        > .scrollable-view {
            flex: 1 1 auto;

            > .scrollable-content {
                padding: 0 25px;
                display: flex;
                flex-direction: column;

                .tip-and-pay-form {
                    flex: 1 1 auto;

                    &.hidden {
                        visibility: hidden;
                    }
                }

                .tip-summary-table {
                    flex: 0 0 auto;
                }
            }
        }

        > footer {
            flex: 0 0 auto;
            padding: 38px 15px 15px;

            box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.25);
            border-top: 1px solid #dcdcdc;

            display: flex;

            .proceed-button {
                -webkit-transition: width 0.4s ease;
                transition: width 0.4s ease;
            }
        }
    }
</style>
